/**
 * @file planesStore.ts
 * @description Global Zustand store for application state management
 * 
 * Manages:
 * - Search and selection filters (in-memory state)
 * - User favorites (synchronized with Firestore)
 * 
 * Pattern: Single store with multiple state slices
 * 
 * UPDATED 25/01/2026: Migrated from local REST API to Firebase Firestore
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Filters, DurationDays, PlanType } from '@/types/plan'
import { toggleFavorite as toggleFavoriteFirestore, getFavorites as getFavoritesFirestore } from '@/lib/api-firestore'

interface PlansStore {
  // ============ STATE ============
  
  /** Active filters (search, types, durations) - Not persisted */
  filters: Filters
  
  /** Plan IDs marked as favorites - Synced with API */
  favorites: number[]
  
  /** Indicates if favorites are being loaded from API */
  loadingFavorites: boolean
  
  // ============ FILTER ACTIONS ============
  
  /**
   * Update search text
   * @param search - Text to search in name, route and activities
   */
  setSearch: (search: string) => void
  
  /**
   * Toggle a plan type in filters
   * @param type - Type to toggle (Montaña, Mar, etc.)
   */
  toggleType: (type: PlanType) => void
  
  /**
   * Toggle a duration in filters
   * @param duration - Number of days (1-5)
   */
  toggleDuration: (duration: DurationDays) => void
  
  /**
   * Clear all active filters
   */
  clearFilters: () => void
  
  // ============ FAVORITES ACTIONS ============
  
  /**
   * Load favorites from API on initialization
   * @returns Promise that resolves when favorites are loaded
   */
  loadFavorites: () => Promise<void>
  
  /**
   * Add or remove plan from favorites (syncs with API)
   * @param id - Plan ID
   */
  toggleFavorite: (id: number) => Promise<void>
  
  /**
   * Check if a plan is in favorites
   * @param id - Plan ID
   * @returns true if it's in favorites
   */
  isFavorite: (id: number) => boolean
}

/** Initial filter state (no filters applied) */
const initialFilters: Filters = {
  search: '',
  types: [],
  durations: [],
}

/**
 * Zustand store with selective persistence
 * 
 * Configuration:
 * - `persist`: Only saves favorites to localStorage with key 'xiavan-storage'
 * - Filters NOT persisted for fresh experience on each visit
 */
export const usePlansStore = create<PlansStore>()(
  persist(
    (set, get) => ({
      // ============ INITIAL STATE ============
      filters: initialFilters,
      favorites: [],
      loadingFavorites: false,

      // ============ FILTER ACTIONS IMPLEMENTATION ============
      
      setSearch: (search) =>
        set((state) => ({
          filters: { ...state.filters, search },
        })),

      toggleType: (type) =>
        set((state) => {
          // If already present, remove it; if not, add it
          const types = state.filters.types.includes(type)
            ? state.filters.types.filter((t) => t !== type)
            : [...state.filters.types, type]
          return {
            filters: { ...state.filters, types },
          }
        }),

      toggleDuration: (duration) =>
        set((state) => {
          // If already present, remove it; if not, add it
          const durations = state.filters.durations.includes(duration)
            ? state.filters.durations.filter((d) => d !== duration)
            : [...state.filters.durations, duration]
          return {
            filters: { ...state.filters, durations },
          }
        }),

      clearFilters: () =>
        set({
          filters: initialFilters,
        }),

      // ============ FAVORITES ACTIONS IMPLEMENTATION ============
      
      /**
       * Load favorites from Firestore when initializing app
       * Called once in App.tsx useEffect
       */
      loadFavorites: async () => {
        try {
          set({ loadingFavorites: true })
          const favoritesFromFirestore = await getFavoritesFirestore()
          set({ favorites: favoritesFromFirestore, loadingFavorites: false })
        } catch (error) {
          console.error('Error loading favorites:', error)
          set({ loadingFavorites: false })
          // Fallback: keep empty favorites if error
        }
      },

      /**
       * Toggle favorite: sync with Firestore and update local state
       * Optimistic update: update local first, then sync
       */
      toggleFavorite: async (id: number) => {
        const state = get()
        
        // Update local state optimistically
        const newFavorites = state.favorites.includes(id)
          ? state.favorites.filter((fav) => fav !== id)
          : [...state.favorites, id]
        
        set({ favorites: newFavorites })
        
        // Sync with Firestore
        try {
          const response = await toggleFavoriteFirestore(id)
          // Confirm with server data
          set({ favorites: response.favoritos })
        } catch (error) {
          console.error('Error updating favorites:', error)
          // Rollback: restore previous state
          set({ favorites: state.favorites })
        }
      },

      isFavorite: (id: number) => get().favorites.includes(id),
    }),
    {
      name: 'xiavan-storage',
      partialize: (state) => ({ favoritos: state.favorites }),
    }
  )
)
